import { PostProcess as BABYLON_PostProcess } from '@inter-ikea-betrakta-babylonjs/core/PostProcesses/postProcess.js';
import { IMatrix4, IVector2, IVector3, IVector4 } from '../../api.js';
import { Matrix as BABYLON_Matrix, Vector3 } from '../../Math.js';
import { Camera as BABYLON_Camera } from '@inter-ikea-betrakta-babylonjs/core/Cameras/camera.js';
export var ShaderDescriptorType;
(function (ShaderDescriptorType) {
    ShaderDescriptorType[ShaderDescriptorType["SAMPLER_2D"] = 0] = "SAMPLER_2D";
    ShaderDescriptorType[ShaderDescriptorType["FLOAT"] = 1] = "FLOAT";
    ShaderDescriptorType[ShaderDescriptorType["INT"] = 2] = "INT";
    ShaderDescriptorType[ShaderDescriptorType["UINT"] = 3] = "UINT";
    ShaderDescriptorType[ShaderDescriptorType["VEC2"] = 4] = "VEC2";
    ShaderDescriptorType[ShaderDescriptorType["VEC3"] = 5] = "VEC3";
    ShaderDescriptorType[ShaderDescriptorType["VEC4"] = 6] = "VEC4";
    ShaderDescriptorType[ShaderDescriptorType["MAT4"] = 7] = "MAT4";
})(ShaderDescriptorType || (ShaderDescriptorType = {}));
export class ShaderDescription {
    constructor(shader_name) {
        this._descriptors = [];
        this.shader_name = shader_name;
    }
    add_descriptor(type, location) {
        const handle = {
            _descriptor_index: this._descriptors.length,
            type: type
        };
        this._descriptors.push({
            type: type,
            location: location,
            value: 0.0,
            has_been_set: false,
            uninitialized_data_has_warned: false
        });
        return handle;
    }
    /** @internal */
    get_descriptors() {
        return this._descriptors;
    }
}
export class PostProcessShader {
    constructor(scene, description, defines = []) {
        // For Babylon when creating the shader
        this._uniform_names = [];
        this._sampler_names = [];
        // Internally we never store Babylon matrices, only IMatrix4
        this._descriptors = [];
        this._scene = scene;
        // Find all samplers and uniform names to give to Babylon
        const descriptors = description.get_descriptors();
        this._descriptors.length = descriptors.length;
        for (let i = 0; i < descriptors.length; i++) {
            const type = descriptors[i].type;
            this._descriptors[i] = Object.assign({}, descriptors[i]);
            if (type === ShaderDescriptorType.SAMPLER_2D) {
                this._sampler_names.push(descriptors[i].location);
            }
            else {
                this._uniform_names.push(descriptors[i].location);
            }
        }
        this._shader_name = description.shader_name;
        this._useless_camera = new BABYLON_Camera('Babylon why do you need a camera for a postprocess: ' + description.shader_name, Vector3.Zero(), scene, false);
        this._effect = this.create_effect(defines);
        // Initialize all descriptor values to their correct types so that we can update them in-place when the user sets them.
        for (let i = 0; i < this._descriptors.length; i++) {
            const descriptor = this._descriptors[i];
            descriptor.has_been_set = false;
            if (descriptor.type === ShaderDescriptorType.SAMPLER_2D) {
                descriptor.value = null;
            }
            else if (descriptor.type === ShaderDescriptorType.FLOAT ||
                descriptor.type === ShaderDescriptorType.INT ||
                descriptor.type === ShaderDescriptorType.UINT) {
                descriptor.value = 0;
            }
            else if (descriptor.type === ShaderDescriptorType.VEC2) {
                descriptor.value = new IVector2(0, 0);
            }
            else if (descriptor.type === ShaderDescriptorType.VEC3) {
                descriptor.value = new IVector3(0, 0, 0);
            }
            else if (descriptor.type === ShaderDescriptorType.VEC4) {
                descriptor.value = new IVector4();
            }
            else if (descriptor.type === ShaderDescriptorType.MAT4) {
                descriptor.value = new IMatrix4();
            }
        }
    }
    set_descriptor(handle, value) {
        if (handle._descriptor_index >= this._descriptors.length) {
            console.error('Shader descriptor index out of range.');
            return;
        }
        const descriptor = this._descriptors[handle._descriptor_index];
        if (descriptor.type !== handle.type) {
            console.error("Shader descriptor type doesn't match given descriptor handle type");
            return;
        }
        if (handle.type === ShaderDescriptorType.SAMPLER_2D) {
            descriptor.value = value;
        }
        else if (handle.type === ShaderDescriptorType.FLOAT ||
            handle.type === ShaderDescriptorType.INT ||
            handle.type === ShaderDescriptorType.UINT) {
            descriptor.value = value;
        }
        else if (handle.type === ShaderDescriptorType.VEC2) {
            IVector2.copy_to_ref(value, descriptor.value);
        }
        else if (descriptor.type === ShaderDescriptorType.VEC3) {
            IVector3.copy_to_ref(value, descriptor.value);
        }
        else if (descriptor.type === ShaderDescriptorType.VEC4) {
            IVector4.copy_to_ref(value, descriptor.value);
        }
        else if (descriptor.type === ShaderDescriptorType.MAT4) {
            // Only ever store as IMatrix4
            const descriptor_matrix = descriptor.value;
            if (value instanceof BABYLON_Matrix) {
                value.toIMatrix4ToRef(descriptor_matrix);
            }
            else {
                IMatrix4.copy_to_ref(value, descriptor_matrix);
            }
        }
        else {
            console.error('Invalid type in descriptor value. Descriptor location: ' + descriptor.location);
        }
        descriptor.has_been_set = true;
    }
    get_descriptor(handle) {
        if (handle._descriptor_index >= this._descriptors.length) {
            throw new Error('Shader descriptor index out of range.');
        }
        const descriptor = this._descriptors[handle._descriptor_index];
        if (descriptor.type !== handle.type) {
            throw new Error("Shader descriptor type doesn't match given descriptor handle type");
        }
        return descriptor.value;
    }
    create_effect(defines) {
        if (this._effect) {
            this._effect.dispose();
        }
        this._effect = new BABYLON_PostProcess(this._shader_name, this._shader_name, this._uniform_names, this._sampler_names, 1.0, this._useless_camera, undefined, this._scene.getEngine(), undefined, defines.join('\n'));
        this._effect.onApply = this.on_apply.bind(this);
        return this._effect;
    }
    on_apply(effect) {
        for (let i = 0; i < this._descriptors.length; i++) {
            const descriptor = this._descriptors[i];
            if (!descriptor.has_been_set) {
                if (!descriptor.uninitialized_data_has_warned) {
                    console.warn("PostProcessShader descriptor hasn't been set: " + descriptor.location + '. This will result in junk in the shader.');
                    descriptor.uninitialized_data_has_warned = true;
                }
                continue;
            }
            if (descriptor.type === ShaderDescriptorType.SAMPLER_2D) {
                effect.setTexture(descriptor.location, descriptor.value);
            }
            else if (descriptor.type === ShaderDescriptorType.FLOAT) {
                effect.setFloat(descriptor.location, descriptor.value);
            }
            else if (descriptor.type === ShaderDescriptorType.INT) {
                effect.setInt(descriptor.location, descriptor.value);
            }
            else if (descriptor.type === ShaderDescriptorType.UINT) {
                effect.setUInt(descriptor.location, descriptor.value);
            }
            else if (descriptor.type === ShaderDescriptorType.VEC2) {
                const v = descriptor.value;
                effect.setFloat2(descriptor.location, v.x, v.y);
            }
            else if (descriptor.type === ShaderDescriptorType.VEC3) {
                const v = descriptor.value;
                effect.setFloat3(descriptor.location, v.x, v.y, v.z);
            }
            else if (descriptor.type === ShaderDescriptorType.VEC4) {
                const v = descriptor.value;
                effect.setFloat4(descriptor.location, v.x, v.y, v.z, v.w);
            }
            else if (descriptor.type === ShaderDescriptorType.MAT4) {
                const matrix = descriptor.value;
                effect.setMatrix(descriptor.location, new BABYLON_Matrix().fromIMatrix4(matrix));
            }
            else {
                console.error('Unknown descriptor type');
            }
        }
    }
    render(target) {
        var _a;
        this._scene.postProcessManager.directRender([this._effect], (_a = target === null || target === void 0 ? void 0 : target.renderTarget) !== null && _a !== void 0 ? _a : null, true, 0, 0, target === null);
    }
    set_defines(defines) {
        this.create_effect(defines);
    }
    dispose() {
        this._effect.dispose();
        this._useless_camera.dispose();
    }
}
//# sourceMappingURL=PostProcessShader.js.map